/** Auth S2 (mesmos utilizadores do Financial) via cookie HttpOnly. */
const API_BASE = (import.meta.env.VITE_FINANCIAL_API_BASE as string) || '/api/v1'

import type { PaineisMap } from '../permissions/panels'

export type BiAuthUser = {
  id: string
  email: string
  nome?: string
  tipo?: string
  paineis?: PaineisMap
}

function apiRoot() {
  return API_BASE.replace(/\/$/, '')
}

export async function authCheck(): Promise<BiAuthUser | null> {
  const res = await fetch(`${apiRoot()}/auth/check`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store',
  })
  if (res.status === 401) return null
  if (!res.ok) throw new Error(`Sessão: HTTP ${res.status}`)
  const json = (await res.json()) as {
    success?: boolean
    data?: { usuarioId?: string; email?: string; usuario?: Record<string, unknown> }
  }
  const data = json.data
  if (!data) return null
  if (data.usuario && typeof data.usuario === 'object') {
    const u = data.usuario
    return {
      id: String(u._id || u.id || data.usuarioId || ''),
      email: String(u.email || data.email || ''),
      nome: u.nome != null ? String(u.nome) : undefined,
      tipo: u.tipo != null ? String(u.tipo) : undefined,
      paineis:
        u.paineis && typeof u.paineis === 'object'
          ? (u.paineis as PaineisMap)
          : undefined,
    }
  }
  if (!data.email && !data.usuarioId) return null
  return {
    id: String(data.usuarioId || ''),
    email: String(data.email || ''),
  }
}

export async function authLogin(email: string, senha: string): Promise<BiAuthUser> {
  const res = await fetch(`${apiRoot()}/auth/login`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ email, senha }),
  })
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean
    data?: { usuario?: Record<string, unknown> }
    error?: { message?: string }
  }
  if (!res.ok || !json.success) {
    throw new Error(json.error?.message || `Login falhou (HTTP ${res.status})`)
  }
  const u = json.data?.usuario
  if (!u) throw new Error('Resposta de login sem utilizador')
  return {
    id: String(u._id || u.id || ''),
    email: String(u.email || email),
    nome: u.nome != null ? String(u.nome) : undefined,
    tipo: u.tipo != null ? String(u.tipo) : undefined,
    paineis:
      u.paineis && typeof u.paineis === 'object' ? (u.paineis as PaineisMap) : undefined,
  }
}

export async function authLogout(): Promise<void> {
  await fetch(`${apiRoot()}/auth/logout`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    cache: 'no-store',
  }).catch(() => {
    /* limpar sessão local mesmo se o POST falhar */
  })
}

export async function authForgotPassword(email: string): Promise<string> {
  const res = await fetch(`${apiRoot()}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ email }),
  })
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean
    message?: string
    error?: { message?: string }
  }
  if (!res.ok || !json.success) {
    throw new Error(json.error?.message || `Pedido falhou (HTTP ${res.status})`)
  }
  return (
    json.message ||
    'Se existir uma conta activa com este e-mail, enviámos instruções para redefinir a senha.'
  )
}

export async function authResetPassword(token: string, senha: string): Promise<string> {
  const res = await fetch(`${apiRoot()}/auth/reset-password`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ token, senha }),
  })
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean
    message?: string
    error?: { message?: string }
  }
  if (!res.ok || !json.success) {
    throw new Error(json.error?.message || `Redefinição falhou (HTTP ${res.status})`)
  }
  return json.message || 'Senha actualizada. Já pode entrar com a nova senha.'
}
